import { type CardLayoutAttributes } from './generated/CardLayoutAttributes';
import { camelize } from 'inflected';
import ApplicationModel, { attr } from '../ApplicationModel';
import { CardLayoutRecordTypesEnum } from '../Enums';
import { model } from '../modelBuilder';
import RecordLayout, { DEFAULT_CARD_VIEWS } from './RecordLayout';
import { HasCardLayoutHierarchy } from './concerns/HasCardLayoutHierarchy';
import { JsonQuerySerialization } from './concerns/JsonQuerySerialization';
import { type CardLayoutModelTypeName } from 'components/CardLayoutEditor/models/types';
import {
  type CardViewType,
  type RecordLayoutKey,
} from 'javascripts/shared-types';
import { type CardLayoutRecordTypesEnum_value } from './generated/types';
import type CardView from './CardView';

const LAYOUT_ORDER: CardLayoutModelTypeName[] = [
  'Goal',
  'Initiative',
  'Release',
  'ReleasePhase',
  'Epic',
  'Feature',
  'Requirement',
  'Task',
  'Idea',
];

interface CardLayout
  extends CardLayoutAttributes,
    Omit<HasCardLayoutHierarchy, 'recordLayout' | 'cardView'> {}

@model
@HasCardLayoutHierarchy
@JsonQuerySerialization
class CardLayout extends ApplicationModel {
  /**
   * Given a card layout and a default card layout, constructs the "actual" card layout being used
   * by loading the default layout for inherited card views.
   *
   * @param cardLayout The main card layout.
   * @param defaultCardLayout The default card layout to inherit from.
   * @return An ad hoc card layout that only uses the actual layouts chosen.
   */
  static buildActiveCardLayout({
    cardLayout,
    defaultCardLayout,
    cardViews = DEFAULT_CARD_VIEWS,
    recordTypes = LAYOUT_ORDER,
  }: {
    cardLayout: CardLayout;
    defaultCardLayout: CardLayout;
    cardViews?: CardViewType[];
    recordTypes?: CardLayoutModelTypeName[];
  }) {
    const recordLayouts = recordTypes.reduce<
      Partial<Record<RecordLayoutKey, RecordLayout>>
    >((layouts, recordType) => {
      const recordLayout = cardLayout.recordLayout(recordType);
      const defaultRecordLayout = defaultCardLayout.recordLayout(recordType);
      if (!recordLayout && !defaultRecordLayout) return layouts;

      const recordLayoutKey = this.recordLayoutKey(recordType);
      layouts[recordLayoutKey] = new RecordLayout(
        cardViews.reduce<
          Pick<RecordLayout, 'recordType'> &
            Partial<Pick<RecordLayout, CardViewType>>
        >(
          (views, key) => {
            let cardView = recordLayout?.[key] || defaultRecordLayout[key];
            const defaultCardView = defaultRecordLayout[key];

            // This shouldn't happen, but we can't add the record layout if we don't have a default
            if (!defaultCardView) return views;

            if (
              !cardView ||
              cardView.inherited ||
              cardView.isEqualTo(defaultCardView)
            ) {
              cardView = defaultCardView.dup();
              cardView.attributes.inherited = true;
            }

            views[key] = cardView.dup();
            return views;
          },
          { recordType }
        ),
        { parent: cardLayout }
      );

      return layouts;
    }, {});

    return new CardLayout(recordLayouts, cardLayout.options);
  }

  static recordLayoutKey(recordType: CardLayoutModelTypeName) {
    return `${camelize(recordType, false)}RecordLayout` as RecordLayoutKey;
  }

  static buildCardLayoutScope({
    recordTypes = CardLayoutRecordTypesEnum.values,
    cardViews = DEFAULT_CARD_VIEWS,
  }: {
    recordTypes?: CardLayoutRecordTypesEnum_value[];
    cardViews?: CardViewType[];
  } = {}) {
    const subqueries = CardLayoutRecordTypesEnum.values.reduce(
      (acc, recordType) => {
        if (!recordTypes.includes(recordType)) return acc;

        acc[this.recordLayoutKey(recordType)] =
          RecordLayout.defaultScope(cardViews);

        return acc;
      },
      {}
    );

    return CardLayout.select('epicsEnabled').merge(subqueries);
  }

  // Start auto-generated
  static typename: 'CardLayout' = 'CardLayout';
  get typename(): 'CardLayout' {
    return 'CardLayout';
  }

  static inputType = 'CardLayoutAttributes';

  static fields = {
    epicsEnabled: attr({ readable: true }),
    epicRecordLayout: attr.belongsTo({
      writeShape: ['expanded', 'collapsed', 'whiteboard'],
      readable: true,
      typename: 'RecordLayout',
    }),
    featureRecordLayout: attr.belongsTo({
      writeShape: ['expanded', 'collapsed', 'whiteboard'],
      readable: true,
      typename: 'RecordLayout',
    }),
    goalRecordLayout: attr.belongsTo({
      writeShape: ['expanded', 'collapsed', 'whiteboard'],
      readable: true,
      typename: 'RecordLayout',
    }),
    ideaRecordLayout: attr.belongsTo({
      writeShape: ['expanded', 'collapsed', 'whiteboard'],
      readable: true,
      typename: 'RecordLayout',
    }),
    initiativeRecordLayout: attr.belongsTo({
      writeShape: ['expanded', 'collapsed', 'whiteboard'],
      readable: true,
      typename: 'RecordLayout',
    }),
    keyResultRecordLayout: attr.belongsTo({
      writeShape: ['expanded', 'collapsed', 'whiteboard'],
      readable: true,
      typename: 'RecordLayout',
    }),
    releasePhaseRecordLayout: attr.belongsTo({
      writeShape: ['expanded', 'collapsed', 'whiteboard'],
      readable: true,
      typename: 'RecordLayout',
    }),
    releaseRecordLayout: attr.belongsTo({
      writeShape: ['expanded', 'collapsed', 'whiteboard'],
      readable: true,
      typename: 'RecordLayout',
    }),
    requirementRecordLayout: attr.belongsTo({
      writeShape: ['expanded', 'collapsed', 'whiteboard'],
      readable: true,
      typename: 'RecordLayout',
    }),
    taskRecordLayout: attr.belongsTo({
      writeShape: ['expanded', 'collapsed', 'whiteboard'],
      readable: true,
      typename: 'RecordLayout',
    }),
  };
  // End auto-generated

  getCardView(
    recordType,
    cardViewType: CardViewType = 'expanded',
    defaultCardLayout?: CardLayout
  ): CardView | undefined {
    const recordLayout = this.recordLayout(recordType);
    if (!recordLayout) return;

    const cardView = recordLayout[cardViewType];
    if ((!cardView || cardView.inherited) && defaultCardLayout) {
      return defaultCardLayout.getCardView(recordType, cardViewType);
    }

    if (cardView) {
      return cardView;
    }
  }

  recordLayout(recordType) {
    return this[CardLayout.recordLayoutKey(recordType)] as RecordLayout;
  }

  availableRecordLayouts() {
    return LAYOUT_ORDER.reduce((recordLayouts, recordType) => {
      const key = CardLayout.recordLayoutKey(recordType);
      if (this[key]) {
        recordLayouts[key] = this[key];
      }

      return recordLayouts;
    }, {});
  }
}

export default CardLayout;
